import React from 'react'
import { XStack, YStack, styled } from 'tamagui'
import { Trans, useLingui } from '@lingui/react/macro'

import { useScreenMessages } from '@data/i18n'
import { Metadata } from '@newComponents/Metadata'
import { H1, H2, H3, Strong, Paragraph, Text } from '@newComponents/Text'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Image } from '@newComponents/Image'
import { Card } from '@newComponents/Card'
import { Link, ExternalLink } from '@newComponents/Link'
import { OrderedList, Li } from '@newComponents/OrderedList/OrderedList'

import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import blueskySettings from './images/guild-bluesky-settings.jpg'
import blueskySettingsMenuItem from './images/bluesky-settings-menu-item.jpg'
import blueskySettingsPrivacyAndSecurity from './images/bluesky-settings-privacy-and-security.jpg'
import blueskySettingsAppPasswords from './images/bluesky-settings-app-passwords.jpg'
import guildBlueskyForm from './images/guild-bluesky-form.jpg'
import blueskySettingsChangeHandle from './images/bluesky-settings-change-handle.jpg'
import blueskySettingsChangeHandleDnsPanel from './images/bluesky-settings-change-handle-dns-panel.jpg'

const StyledImage = ({ src }: { src: string }) => {
  return (
    <XStack justifyContent='center'>
      <Image
        src={src}
        width='100%'
        maxWidth={500}
        borderWidth={1}
        borderColor='$borderColor'
      />
    </XStack>
  )
}

export const BlueskyIntegrationScreen = () => {
  const { t } = useLingui()
  useScreenMessages((locale) => import(/* webpackChunkName: "locale-bluesky-integration-screen-[request]" */ `./locales/${locale}.po`))

  return (
    <>
      <Metadata title={t`Bluesky Integration`} />

      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$8'>
          <Section>
            <H1><Trans>Bluesky Integration</Trans> 🦋</H1>

            <YStack gap='$6'>
              <Paragraph>
                <Trans>
                  Bluesky is an awesome new social media platform with a big focus
                  on community, safety, and decentralization. These are values
                  that resonate with us at Guild.
                </Trans>
              </Paragraph>

              <Paragraph>
                <Trans>
                  That's why we're committed to integrating with it, and are
                  working to make your experience on both as seamless as possible.
                </Trans>
              </Paragraph>

              <Paragraph><Trans>Read along for more</Trans> 👇</Paragraph>
            </YStack>
          </Section>

          <Section>
            <H2><Trans>Features</Trans></H2>

            <OrderedList gap='$2'>
              <Li>
                <YStack gap='$2'>
                  <Paragraph>
                    <Trans>
                      Make{' '}
                      <Text fontWeight='$6'>
                        {`<your-guilds-short-id>.guild.host`}
                      </Text>{' '}
                      available for you to use as your Bluesky handle.
                    </Trans>
                  </Paragraph>

                  <Paragraph>
                    <Trans>
                      E.g.:{' '}
                      <ExternalLink
                        fontWeight='$6'
                        href='https://bsky.app/profile/montreal-tech.guild.host'
                      >
                        @montreal-tech.guild.host
                      </ExternalLink>{' '}
                      is the handle for the{' '}
                      <Link href='/montreal-tech'>Montreal Tech Network</Link>.
                    </Trans>
                  </Paragraph>
                </YStack>
              </Li>

              <Li>
                <Paragraph>
                  <Trans>
                    Automatically post an announcement when an Event is Listed.
                    Including:
                  </Trans>
                </Paragraph>

                <OrderedList
                  gap='$1'
                  marginTop='$2'
                  style={{ listStyleType: 'lower-alpha' }}
                >
                  <Li>
                    <Paragraph><Trans>Start time</Trans></Paragraph>
                  </Li>
                  <Li>
                    <Paragraph>
                      <Trans>Event Title with an inline Link to Event</Trans>
                    </Paragraph>
                  </Li>
                  <Li>
                    <Paragraph><Trans>AI Summary of the Event's Description</Trans></Paragraph>
                  </Li>
                  <Li>
                    <Paragraph>
                      <Trans>Short Shareable URL linking to the Event</Trans>
                    </Paragraph>
                  </Li>
                </OrderedList>
              </Li>
            </OrderedList>
          </Section>

          <Section>
            <H2><Trans>How to set up the Bluesky integration</Trans></H2>

            <H3><Trans>Step 1: Get an App Password from Bluesky</Trans></H3>

            <Paragraph>
              <Trans>
                You'll be asked to enter you Bluesky username (eg.
                @devsphere.bsky.social) and an App Password.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                An App Password is a feature from Bluesky that allows you to
                create a password specifically for this integration.
              </Trans>
            </Paragraph>

            <YStack gap='$2'>
              <Paragraph textDecorationLine='underline'>
                <Trans>Do not enter your Bluesky account password!</Trans>
              </Paragraph>

              <Paragraph size='$4'>
                <Trans>This isn't just for our integration.</Trans>
              </Paragraph>

              <Paragraph size='$4'>
                <Trans>
                  As general advice, it's a good practice to use an App Password
                  for any third-party integration for Bluesky.
                </Trans>
              </Paragraph>
            </YStack>

            <ExternalLink
              href='https://bsky.app/settings/app-passwords'
              fontWeight='$6'
            >
              <Trans>Click here to go straight to your Bluesky App Passwords screen</Trans>
            </ExternalLink>

            <H3><Trans>Or, navigate to the App Passwords screen manually:</Trans></H3>

            <Paragraph>
              <Trans>Head over to <Text fontWeight='$5'>"Settings"</Text>:</Trans>
            </Paragraph>

            <StyledImage src={blueskySettingsMenuItem} />

            <Paragraph>
              <Trans>
                Select the <Text fontWeight='$5'>"Privacy and security"</Text>{' '}
                tab:
              </Trans>
            </Paragraph>

            <StyledImage src={blueskySettingsPrivacyAndSecurity} />

            <Paragraph>
              <Trans>
                Select the <Text fontWeight='$5'>"App passwords"</Text> tab:
              </Trans>
            </Paragraph>

            <StyledImage src={blueskySettingsAppPasswords} />

            <Paragraph>
              <Trans>
                From there, click{' '}
                <Text fontWeight='$5'>"+ Add App Password"</Text>, give your App
                Password a name so you can remember what you used it for (e.g.
                "Guild Integration"), do not allow access to Direct Messages, and
                hit <Text fontWeight='$5'>"Next"</Text>.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                You should see a screen that has your new App Password. Keep a
                record of this for the next step!
              </Trans>
            </Paragraph>
          </Section>

          <Section>
            <H3><Trans>Step 2: Access the Bluesky tab in your Guild's Settings</Trans></H3>

            <Paragraph><Trans>Go to your Guild's Settings screen.</Trans></Paragraph>

            <Paragraph>
              <Trans>
                Click on the <Text fontWeight='$5'>"Bluesky"</Text> tab on the
                left hand side.
              </Trans>
            </Paragraph>

            <StyledImage src={blueskySettings} />
          </Section>

          <Section>
            <H3><Trans>Step 3: Enter your Bluesky Handle and App Password</Trans></H3>

            <Paragraph><Trans>You'll be presented with the following form:</Trans></Paragraph>

            <StyledImage src={guildBlueskyForm} />

            <Paragraph>
              <Trans>
                Please enter your Bluesky username or DID (e.g.{' '}
                <Text fontWeight='$5'>@devsphere.bsky.social</Text>) and the App
                Password you received from Step 1.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                Hit the <Text fontWeight='$5'>"Save"</Text> button and you're all
                done!
              </Trans>
            </Paragraph>

            <YStack gap='$3'>
              <Paragraph size='$4'>
                <Trans>
                  If you forgot to save your App Password in a place where you can
                  re-use it, don't worry! You can always go back and delete the
                  old App Password, create a new one, and drop it in here.
                </Trans>
              </Paragraph>

              <Paragraph size='$4'>
                <Trans>
                  That's the benefit to using App Passwords: you can revoke them
                  at any time!
                </Trans>
              </Paragraph>
            </YStack>
          </Section>

          <Section>
            <H3><Trans>Step 4: Use your new Guild.Host Handle within Bluesky</Trans></H3>

            <ExternalLink
              href='https://bsky.app/settings/account'
              fontWeight='$6'
            >
              <Trans>Click here to go directly to Bluesky's Account Settings Screen</Trans>
            </ExternalLink>

            <Paragraph>
              <Trans>
                Or, manually go to Bluesky's Settings screen. Then select the
                <Text fontWeight='$5'>"Account"</Text> tab
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                Next, select the <Text fontWeight='$5'>"Handle"</Text> option.
              </Trans>
            </Paragraph>

            <Paragraph>
              <Trans>
                Then select <Text fontWeight='$5'>"I have my own domain"</Text> at
                the bottom of the form:
              </Trans>
            </Paragraph>

            <StyledImage src={blueskySettingsChangeHandle} />

            <Paragraph>
              <Trans>
                Then enter the domain you want to use, in this case it's{' '}
                <Text fontWeight='$6'>{`<your-guilds-short-id>.guild.host`}</Text>{' '}
                and select <Text fontWeight='$5'>"No DNS Panel"</Text>:
              </Trans>
            </Paragraph>

            <StyledImage src={blueskySettingsChangeHandleDnsPanel} />

            <XStack gap='$1'>
              <Paragraph>
                <Trans>
                  Then select <Text fontWeight='$5'>"Verify Text File"</Text> and
                  you should be all done!
                </Trans>
              </Paragraph>

              <EmojiIcon name='party-popper'></EmojiIcon>
            </XStack>
          </Section>

          <Section>
            <H3><Trans>Enjoy your new Bluesky integration!</Trans></H3>

            <XStack gap='$1'>
              <Paragraph>
                <Trans>
                  Now, whenever a new Event is Listed across your Guild or
                  Network, we'll automatically post an announcement to your
                  Bluesky that uses AI to entice folks to sign up!
                </Trans>
              </Paragraph>
            </XStack>

            <XStack gap='$1'>
              <Paragraph>
                <Trans>We look forward to seeing what you get up to!</Trans>
              </Paragraph>
              <EmojiIcon name='red-heart' />
            </XStack>

            <XStack gap='$1'>
              <Paragraph>
                <Trans>
                  We're always available at{' '}
                  <ExternalLink href='mailto:hi@guild.host'>
                    hi@guild.host
                  </ExternalLink>{' '}
                  if you need anything
                </Trans>
              </Paragraph>

              <EmojiIcon name='raising-hands' />
            </XStack>
          </Section>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

export default BlueskyIntegrationScreen

const Section = styled(Card, {
  tag: 'section',
  gap: '$6',
  maxWidth: 600,
  width: '100%',
})
